import { Button, Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { tracker } from "@openreplay/tracker";
import { useAppDispatch } from "@store/hooks";
import { resetNewJobForm } from "@store/slices/newJobFormSlice";
import { dump as yamlDump } from "js-yaml";
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { JOBS_EVENTS } from "@helpers/events";
import { normalizePathname } from "@helpers/utils";

import type { LocalJob } from "../data/localTypes";

const JobInfoContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
}));

const JobInfoHeader = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.grey[200],
  margin: -1,
  marginBottom: 0,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const JobInfoContent = styled("pre")(({ theme }) => ({
  padding: theme.spacing(2),
  margin: 0,
  "& code": {
    fontFamily: '"Courier New", monospace',
    whiteSpace: "pre-wrap",
  },
}));

type Props = {
  job: LocalJob;
};

function JobDetailInfo({ job }: Props) {
  const basename = normalizePathname(useLocation().pathname)
    .split("/")
    .slice(0, -2)
    .join("/");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const jobInfoYaml = useMemo(
    () =>
      yamlDump(job, {
        sortKeys: true,
        skipInvalid: true,
      }),
    [job],
  );

  return (
    <JobInfoContainer variant="outlined">
      <JobInfoHeader variant="outlined">
        <Typography variant="body1">Full job info</Typography>
        <Button
          color="primary"
          onClick={() => {
            tracker.event(JOBS_EVENTS.DETAIL_USE_AS_TEMPLATE_CLICKED);
            dispatch(resetNewJobForm());
            navigate(`${basename}/new?from-id=${job.fullName || job.id}`, {
              state: {
                canGoBack: true,
                fromId: job.id,
              },
            });
          }}
        >
          Use as template for new job
        </Button>
      </JobInfoHeader>
      <JobInfoContent>
        <code>{jobInfoYaml}</code>
      </JobInfoContent>
    </JobInfoContainer>
  );
}

export default JobDetailInfo;
